import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getActivityToday } from '../../services/activityService'
import type { ActivityTodayResponse, BranchActivity, MechanicActivity } from '../../types'
import './ActivityTodayPage.css'

function formatUtcPeriod(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `from ${pad(d.getUTCDate())}.${pad(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} 00:00 UTC`
}

function formatGeneratedAt(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function BranchSection({ branch }: { branch: BranchActivity }) {
  const { t } = useTranslation()
  const totals = branch.mechanics.reduce<MechanicActivity>(
    (acc, m) => ({
      mechanicId: '',
      mechanicName: '',
      totalOrders: acc.totalOrders + m.totalOrders,
      reportsSent: acc.reportsSent + m.reportsSent,
      processing: acc.processing + m.processing,
      aiErrors: acc.aiErrors + m.aiErrors,
    }),
    { mechanicId: '', mechanicName: '', totalOrders: 0, reportsSent: 0, processing: 0, aiErrors: 0 }
  )

  return (
    <div className="activity-branch">
      <h2 className="activity-branch__name">{branch.branchName}</h2>
      <div className="admin-table">
        <div className="admin-table__head activity-table__cols">
          <span>{t('admin.activity.col_mechanic')}</span>
          <span>{t('admin.activity.col_total')}</span>
          <span>{t('admin.activity.col_sent')}</span>
          <span>{t('admin.activity.col_processing')}</span>
          <span>{t('admin.activity.col_errors')}</span>
        </div>
        {branch.mechanics.map(m => (
          <div key={m.mechanicId} className="admin-table__row activity-table__cols" style={{ cursor: 'default' }}>
            <span className="admin-table__primary">{m.mechanicName}</span>
            <span>{m.totalOrders}</span>
            <span>{m.reportsSent}</span>
            <span>{m.processing}</span>
            <span>
              {m.aiErrors > 0 ? (
                <span className="activity-error-badge">{m.aiErrors}</span>
              ) : (
                <span className="activity-zero">0</span>
              )}
            </span>
          </div>
        ))}
        <div className="admin-table__row activity-table__cols activity-totals-row" style={{ cursor: 'default' }}>
          <span className="activity-totals-row__label">{t('admin.activity.row_total')}</span>
          <span>{totals.totalOrders}</span>
          <span>{totals.reportsSent}</span>
          <span>{totals.processing}</span>
          <span>
            {totals.aiErrors > 0 ? (
              <span className="activity-error-badge">{totals.aiErrors}</span>
            ) : (
              <span className="activity-zero">0</span>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}

export default function ActivityTodayPage() {
  const { t } = useTranslation()
  const [data, setData] = useState<ActivityTodayResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const refreshingRef = useRef(false)

  async function fetchData(isRefresh = false) {
    if (refreshingRef.current) return
    refreshingRef.current = true

    if (isRefresh) setRefreshing(true)
    else setLoading(true)
    setError(null)

    try {
      const res = await getActivityToday()
      setData(res)
    } catch {
      setError(t('admin.activity.load_failed'))
    } finally {
      setLoading(false)
      setRefreshing(false)
      refreshingRef.current = false
    }
  }

  useEffect(() => { fetchData() }, [])

  return (
    <div className="activity-page">
      <div className="activity-page__header">
        <div>
          <h1 className="activity-page__title">{t('admin.activity.title')}</h1>
          {data && (
            <div className="activity-page__subtitle">{formatUtcPeriod(data.periodStart)}</div>
          )}
        </div>
        <div className="activity-page__right">
          {data && (
            <span className="activity-page__updated">{t('admin.activity.updated', { time: formatGeneratedAt(data.generatedAt) })}</span>
          )}
          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
          >
            {refreshing ? t('admin.activity.refreshing') : t('admin.activity.refresh')}
          </button>
        </div>
      </div>

      {/* Primary loading */}
      {loading && (
        <div className="activity-skeletons">
          {[1, 2].map(i => (
            <div key={i} className="activity-skeleton-card">
              <div className="activity-skeleton-card__title" />
              {[1, 2, 3].map(j => <div key={j} className="activity-skeleton-card__row" />)}
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="audit-error">
          {error}
          <button className="admin-btn admin-btn--secondary" onClick={() => fetchData()}>Retry</button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && data && data.branches.length === 0 && (
        <div className="audit-empty">
          <div className="audit-empty__icon">📊</div>
          <div>No activity today. Data will appear after the first orders are created.</div>
        </div>
      )}

      {/* Content */}
      {!loading && !error && data && data.branches.length > 0 && (
        <div className={`activity-content${refreshing ? ' activity-content--refreshing' : ''}`}>
          {refreshing && <div className="activity-refresh-overlay"><div className="activity-refresh-spinner" /></div>}
          {data.branches.map(branch => (
            <BranchSection key={branch.branchId} branch={branch} />
          ))}
        </div>
      )}
    </div>
  )
}
